import {
  Component, ComponentInterface, Host, Prop, State, h, Element,
} from '@stencil/core';
import packageJson from '../../../../../package.json';
import {
  PropKeys,
  PBSStore,
  getSettings,
  updateStorage,
} from './store';
import {
  PES_SPRYKER_ENDPOINTS,
  APP_TYPES,
  PAGES,
  DIR_TYPES,
} from './constants';

@Component({
  tag: 'pes-testing-control-panel',
  shadow: true,
  styleUrl: 'pes-testing-control-panel.scss',
})
export class TestingControlPanel implements ComponentInterface {
  @Prop() isOpen = true;

  @Element() el: HTMLElement;

  constructor() {
    this.el.dir = document.documentElement.dir || 'auto';
  }

  @State() importedSettings = '';

  @State() importExpanded = false;

  onSettingsChange(
    e: Event | InputEvent,
    setting: PropKeys,
    castCallback?: (value: any) => any,
  ): void {
    // eslint-disable-next-line no-console
    console.log(JSON.stringify({ setting, value: (e as InputEvent)?.detail }));

    if ((e as InputEvent)?.detail !== undefined) {
      const value = (e as InputEvent).detail.toString();
      PBSStore.set(setting, castCallback ? castCallback(value) : value);
    } else if ((e?.target as HTMLInputElement)?.type === 'checkbox') {
      const value = e.target && (e.target as HTMLInputElement).checked;
      PBSStore.set(setting, castCallback ? castCallback(value) : value);
    } else {
      const value = e?.target && (e.target as HTMLInputElement).value;
      PBSStore.set(setting, castCallback ? castCallback(value) : value);
    }
  }

  private onImport = () => {
    if (this.importedSettings) {
      updateStorage(this.importedSettings);
      window.location.reload();
    }
  };

  private onImportChange = (e: any) => {
    this.importedSettings = e.target.value;
  };

  private onImportToggle = () => {
    this.importExpanded = !this.importExpanded;
  };

  toggleOpen(): void {
    this.isOpen = !this.isOpen;
  }

  render(): HTMLElement {
    return (
      <Host class={this.isOpen ? 'open' : ''}>
        <h2 onClick={() => this.toggleOpen()}>Testing control panel</h2>
        <section class="settings--wide version--section">
          <label>
            Package-version: {packageJson.name} - {packageJson.version}
          </label>
        </section>
        <section class="settings--wide">
          <label>Application type:</label>
          <pes-radio
            group-id="appType"
            items={APP_TYPES}
            onPesRadioChange={(e) => this.onSettingsChange(e, 'appType')}
            selected-value={PBSStore.get('appType')}
          />
        </section>

        <section class="settings--wide">
          <label>Host (clear input and click to see all options):</label>
          <input
            class="input--wide"
            type="text"
            list="url"
            onChange={(e) => this.onSettingsChange(e, 'host')}
            value={PBSStore.get('host')}
          />
          <datalist id="url">
            {PES_SPRYKER_ENDPOINTS.map((url, index) => <option key={index}>{url}</option>)}
          </datalist>
        </section>

        <section class="settings--wide settings--extra">
          {PBSStore.get('appType') === 'pesCatalog' && [
            <section>
              <input
                type="checkbox"
                onChange={(e) => this.onSettingsChange(e, 'usePesRouter')}
                checked={PBSStore.get('usePesRouter')}
              />
              <label>Use router parser</label>
            </section>,
          ]}
          {PBSStore.get('appType') === 'pesCatalog' && PBSStore.get('usePesRouter') && (
            <section>
              <label>Routing type:</label>
              <pes-radio
                group-id="routing-type"
                items={[
                  { text: 'path', value: 'path' },
                  { text: 'hash', value: 'hash' },
                ]}
                onPesRadioChange={(e) => this.onSettingsChange(e, 'pesRouterNavigation')}
                selected-value={PBSStore.get('pesRouterNavigation')}
              />
            </section>
          )}
        </section>
        <section class="settings--wide">
          {PBSStore.get('appType') === 'pesCatalog' && [
            !PBSStore.get('usePesRouter') && [
              <section class="settings--wide settings--extra settings--outline">
                <details class="settings--wide">
                  <summary>Page (click to expand)</summary>
                  <pes-radio
                    class="radio--column"
                    group-id="page"
                    items={PAGES}
                    onPesRadioChange={(e) => this.onSettingsChange(e, 'page')}
                    selected-value={PBSStore.get('page')}
                  />
                </details>
                <section class="settings--wide">
                  <section class="settings--wide">
                    <label>Page Id</label>
                    <input
                      type="text"
                      onChange={(e) => this.onSettingsChange(e, 'pageId')}
                      value={PBSStore.get('pageId')}
                    />
                  </section>
                  <section class="settings--wide">
                    <label>Search Params</label>
                    <input
                      type="text"
                      onChange={(e) => this.onSettingsChange(e, 'searchParams')}
                      value={PBSStore.get('searchParams')}
                    />
                  </section>
                </section>
              </section>,
            ],
          ]}
          <section class="settings--wide settings--extra settings--outline">
            {(PBSStore.get('appType') === 'pesCatalog'
              || PBSStore.get('appType') === 'commerceConnector') && [
              !PBSStore.get('usePesRouter') && [
                <section class="settings--wide">
                  <label>Country</label>
                  <input
                    type="text"
                    onChange={(e) => this.onSettingsChange(e, 'country')}
                    value={PBSStore.get('country')}
                  />
                </section>,
                <section class="settings--wide">
                  <label>Language</label>
                  <input
                    type="text"
                    onChange={(e) => this.onSettingsChange(e, 'language')}
                    value={PBSStore.get('language')}
                  />
                </section>,
              ],
            ]}
          </section>
        </section>
        <section class="settings--wide settings--extra">
          {PBSStore.get('appType') === 'pesCatalog' && [
            <section>
              <input
                type="checkbox"
                onChange={(e) => this.onSettingsChange(e, 'showDocuments')}
                checked={PBSStore.get('showDocuments')}
              />
              <label>Show documents</label>
            </section>,
            <section class="settings--wide">
              <label>DDC appSource:</label>
              <input
                type="text"
                onChange={(e) => this.onSettingsChange(e, 'ddcAppSource')}
                value={PBSStore.get('ddcAppSource')}
              />
            </section>,
            <section class="settings--wide">
            <label>DDC access token:</label>
            <input
              type="text"
              onChange={(e) => this.onSettingsChange(e, 'ddcAccessToken')}
              value={PBSStore.get('ddcAccessToken')}
            />
          </section>,
          <section class="settings--wide">
            <label>DDC user profile:</label>
            <input
              type="text"
              onChange={(e) => this.onSettingsChange(e, 'ddcUserProfile')}
              value={PBSStore.get('ddcUserProfile')}
            />
          </section>,
            <section>
              <input
                type="checkbox"
                onChange={(e) => this.onSettingsChange(e, 'showCompare')}
                checked={PBSStore.get('showCompare')}
              />
              <label>Show compare control</label>
            </section>,
            <section class="settings--wide">
              <label title="(all-products)">DDC URL:</label>
              <input
                type="text"
                onChange={(e) => this.onSettingsChange(e, 'ddcAllProductsUrl')}
                value={PBSStore.get('ddcAllProductsUrl')}
              />
            </section>,
            <section>
              <input
                type="checkbox"
                onChange={(e) => this.onSettingsChange(e, 'showFaq')}
                checked={PBSStore.get('showFaq')}
              />
              <label>Show FAQ</label>
            </section>,
            <section>
              <div>
                <label>Show buy online cta:</label>
              </div>
              <pes-radio
                group-id="showBuyOnline"
                items={[
                  { text: 'view details', value: 'false' },
                  { text: 'show buy online', value: 'true' },
                ]}
                onPesRadioChange={(e) => this.onSettingsChange(e, 'showBuyOnline')}
                selected-value={PBSStore.get('showBuyOnline').toString()}
              />
            </section>,
            <section>
              <input
                type="checkbox"
                onChange={(e) => this.onSettingsChange(e, 'showBreadcrumbs')}
                checked={PBSStore.get('showBreadcrumbs')}
              />
              <label>Show breadcrumbs</label>
            </section>,
            <section>
              <label>HTML dir:</label>
              <pes-radio
                group-id="dir"
                items={DIR_TYPES}
                onPesRadioChange={(e) => this.onSettingsChange(e, 'dir')}
                selected-value={PBSStore.get('dir')}
              />
            </section>,
            <section>
              <input
                type="checkbox"
                onChange={(e) => this.onSettingsChange(e, 'showDiscountedPrice')}
                checked={PBSStore.get('showDiscountedPrice')}
              />
              <label>Show Discounted Price</label>
            </section>,
            <section>
              <label>Endpoint:</label>
              <input
                type="text"
                onChange={(e) => this.onSettingsChange(e, 'endpoint')}
                value={PBSStore.get('endpoint')}
              />
            </section>,
            <section>
             <input
               type="checkbox"
               onChange={(e) => this.onSettingsChange(e, 'showCAASContentSlot')}
               checked={PBSStore.get('showCAASContentSlot')}
             />
             <label title="showCAASContentSlot">Show CAAS Overview tab</label>
           </section>,
            <section>
              <label>Site Type:</label>
              <select onChange={(e) => this.onSettingsChange(e, 'siteType')}>
                <option selected={PBSStore.get('siteType') === 'b2b'} value="b2b">
                  B2B
                </option>
                <option selected={PBSStore.get('siteType') === 'b2c'} value="b2c">
                  B2C
                </option>
              </select>
            </section>,
            <section>
              <input
                type="checkbox"
                onChange={(e) => this.onSettingsChange(e, 'showRecentlyViewed')}
                checked={PBSStore.get('showRecentlyViewed')}
              />
              <label>Show recently viewed</label>
            </section>,
            PBSStore.get('usePesRouter') && (
              <section>
                <label>base_dir:</label>
                <input
                  type="text"
                  onChange={(e) => this.onSettingsChange(e, 'base_dir')}
                  value={PBSStore.get('base_dir')}
                />
              </section>
            ),
          ]}
          {PBSStore.get('appType') === 'commerceConnector' && (
            <div>
              <section>
                <label>Products obj</label>
                <textarea
                  rows={10}
                  cols={50}
                  onChange={(e) => this.onSettingsChange(e, 'products')}
                  value={PBSStore.get('products')}
                />
              </section>
              <section>
                <label>Additional distributors obj</label>
                <textarea
                  rows={10}
                  cols={50}
                  onChange={(e) => this.onSettingsChange(e, 'additionalDistributors')}
                  value={PBSStore.get('additionalDistributors')}
                />
              </section>
            </div>
          )}
          <section>
            {this.importExpanded ? (
              <div>
                <label>Import / Export</label>
                <textarea onBlur={this.onImportChange}>{getSettings()}</textarea>
                <button onClick={this.onImport} disabled={!this.importedSettings}>
                  Import
                </button>
                <button onClick={this.onImportToggle}>Close</button>
              </div>
            ) : (
              <button onClick={this.onImportToggle}>Import / Export settings</button>
            )}
          </section>
        </section>
      </Host>
    );
  }
}
